import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Animated, FlatListProps, View } from 'react-native'

import { BannerType } from '@ambire-common/interfaces/banner'
import { Network } from '@ambire-common/interfaces/network'
import { getCurrentAccountBanners } from '@ambire-common/libs/banners/banners'
import InfoIcon from '@common/assets/svg/InfoIcon'
import Banner from '@common/components/Banner'
import Button from '@common/components/Button'
import Spinner from '@common/components/Spinner'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import ActivityPositionsSkeleton from '@common/modules/dashboard/components/Activity/ActivityPositionsSkeleton'
import DashboardBanners from '@common/modules/dashboard/components/DashboardBanners'
import DashboardPageScrollContainer from '@common/modules/dashboard/components/DashboardPageScrollContainer'
import TabsAndSearch from '@common/modules/dashboard/components/TabsAndSearch'
import { TabType } from '@common/modules/dashboard/components/TabsAndSearch/Tabs/Tab/Tab'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import useActivityControllerState from '@web/hooks/useActivityControllerState'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import PrivateActivityRow from '@mobile/modules/dashboard/screens/home/components/PrivateActivityRow'
import { getUiType } from '@web/utils/uiType'

import styles from './styles'
import useRailgunContext from '@web/hooks/useRailgunContext'
import { TransactionHistoryItem } from '@railgun-community/shared-models'

interface Props {
  openTab: TabType
  setOpenTab: React.Dispatch<React.SetStateAction<TabType>>
  initTab?: { [key: string]: boolean }
  sessionId: string
  onScroll: FlatListProps<any>['onScroll']
  animatedOverviewHeight: Animated.Value
  network: Network | null
}

const { isPopup } = getUiType()

const ITEMS_PER_PAGE = 10

export type PrivateHistoryItem = TransactionHistoryItem & {
  networkName: import('@railgun-community/shared-models').NetworkName
}

const ActivityPositions: FC<Props> = ({
  openTab,
  sessionId,
  setOpenTab,
  initTab,
  onScroll,
  animatedOverviewHeight
}) => {
  const { t } = useTranslation()
  const { theme } = useTheme()

  const {
    currentRailgunWallet,
    privateHistory,
    isPrivateHistoryLoading,
    privateHistoryError,
    refreshPrivateHistory
  } = useRailgunContext()

  const { banners } = useActivityControllerState()
  const { account } = useSelectedAccountControllerState()

  const currentAccountBanners = useMemo(() => {
    return getCurrentAccountBanners(banners, account?.addr)
  }, [banners, account])

  const isInitialized = currentRailgunWallet !== undefined && currentRailgunWallet !== null
  const [itemsToShow, setItemsToShow] = useState(ITEMS_PER_PAGE)

  const visibleHistory = useMemo(() => {
    return privateHistory.slice(0, itemsToShow)
  }, [privateHistory, itemsToShow])
  const historyLength = privateHistory.length

  useEffect(() => {
    if (openTab === 'activity') {
      if (!isInitialized) return
      setItemsToShow(ITEMS_PER_PAGE)
      void refreshPrivateHistory()
    }
  }, [openTab, refreshPrivateHistory, isInitialized])

  const renderItem = useCallback(
    ({ item }: any) => {
      if (item === 'header') {
        return (
          <View style={{ backgroundColor: theme.homeCardBackground as string, paddingTop: 0 }}>
            <TabsAndSearch
              openTab={openTab}
              setOpenTab={setOpenTab}
              currentTab="activity"
              sessionId={sessionId}
            />

            {!!currentAccountBanners.length && (
              <View style={spacings.mbMi}>
                {currentAccountBanners.map((banner) => (
                  <Banner
                    key={banner.id}
                    type={banner.type as BannerType}
                    CustomIcon={() => {
                      return (
                        <View style={[flexbox.alignCenter, flexbox.justifyCenter]}>
                          {`${banner.type}` === 'info2' ? (
                            <Spinner style={{ width: 20, height: 20 }} variant="info2" />
                          ) : (
                            <View
                              style={{
                                width: 20,
                                height: 20,
                                borderWidth: 2,
                                borderRadius: 50,
                                borderColor: theme[`${banner.type as BannerType}Decorative`]
                              }}
                            />
                          )}
                          <Text
                            fontSize={12}
                            weight="semiBold"
                            style={{ position: 'absolute' }}
                            appearance={`${banner.type as BannerType}Text`}
                          >
                            {banner.meta!.accountOpsCount}
                          </Text>
                        </View>
                      )
                    }}
                    title={banner.title}
                    text={banner.text}
                    style={{ minHeight: 28, ...spacings.mbTy }}
                    contentContainerStyle={{ minHeight: 28 }}
                  />
                ))}
              </View>
            )}
          </View>
        )
      }

      if (item === 'empty') {
        return (
          <View style={styles.noPositionsWrapper}>
            <InfoIcon width={32} height={32} color={theme.info3Decorative} style={spacings.mtSm} />
            {/* No on-chain explorer link here: shielded RAILGUN transactions are
                never visible on a public block explorer, so pointing the user
                there would be misleading. */}
            <Text
              testID="no-transaction-history-text"
              fontSize={16}
              weight="medium"
              style={styles.noPositions}
            >
              {privateHistoryError
                ? t('Failed to load transaction history.')
                : t('No private transaction history yet.')}
            </Text>
          </View>
        )
      }

      if (!initTab?.activity || !item || item === 'keep-this-to-avoid-key-warning') return null

      if (item === 'skeleton') {
        return <ActivityPositionsSkeleton amount={4} />
      }

      if (item === 'load-more') {
        const hasMoreTxnToLoad = historyLength > itemsToShow

        if (!hasMoreTxnToLoad) return null
        if (isPrivateHistoryLoading) return null

        return (
          <View>
            <Button
              type="secondary"
              size="small"
              style={[flexbox.alignSelfCenter, spacings.mbSm]}
              onPress={() => {
                setItemsToShow((n) => Math.min(n + ITEMS_PER_PAGE, historyLength))
              }}
              text={t('Show more')}
            />
          </View>
        )
      }

      return (
        <View key={(item as PrivateHistoryItem).txid} style={spacings.mbSm}>
          <PrivateActivityRow item={item as PrivateHistoryItem} />
        </View>
      )
    },
    [
      initTab?.activity,
      theme,
      openTab,
      setOpenTab,
      sessionId,
      currentAccountBanners,
      t,
      account,
      privateHistoryError,
      historyLength,
      itemsToShow,
      isPrivateHistoryLoading
    ]
  )

  const keyExtractor = useCallback((positionOrElement: any) => {
    if (typeof positionOrElement === 'string') return positionOrElement

    return positionOrElement.txid ?? positionOrElement.id
  }, [])

  const data = useMemo(() => {
    const isColdLoad = isPrivateHistoryLoading && historyLength === 0
    const base: any[] = [
      'header',
      isColdLoad ? 'skeleton' : 'keep-this-to-avoid-key-warning'
    ]

    if (!isColdLoad) {
      if (initTab?.activity && visibleHistory.length) {
        base.push(...visibleHistory)
      }
      if (initTab?.activity && !visibleHistory.length && !isPrivateHistoryLoading) {
        base.push('empty')
      }
    }

    base.push('load-more')
    return base
  }, [initTab?.activity, visibleHistory, historyLength, isPrivateHistoryLoading])

  return (
    <View style={styles.root}>
      <DashboardPageScrollContainer
        tab="activity"
        openTab={openTab}
        ListHeaderComponent={<DashboardBanners />}
        data={data}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        onEndReachedThreshold={isPopup ? 5 : 2.5}
        initialNumToRender={isPopup ? 10 : 20}
        windowSize={9} // Larger values can cause performance issues.
        onScroll={onScroll}
        animatedOverviewHeight={animatedOverviewHeight}
      />
    </View>
  )
}

export default React.memo(ActivityPositions)
